import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRightLeft,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import { formatDate } from '../utils/helpers';

const STATUS_COLORS = {
  COMMITTED: 'badge-success',
  RESERVED:  'bg-blue-100 text-blue-800',
  ABORTED:   'badge-danger',
  EXPIRED:   'badge-warning',
  PENDING:   'badge-info',
  ERROR:     'badge-danger',
};

const Field = ({ label, value, mono = false }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className={`mt-1 text-sm text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>
      {value ?? <span className="text-gray-400">—</span>}
    </dd>
  </div>
);

const TransferDetail = () => {
  const { id } = useParams();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await mojaloopApi.getTransferById(id);
        setTransfer(data);
      } catch (err) {
        console.error('[TransferDetail] load error:', err);
        setError('Transfer not found or failed to load.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading transfer…</p>
        </div>
      </div>
    );
  }

  if (error || !transfer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Transfer Not Found</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/transfers" className="btn-primary">Back to Transfers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/transfers" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              Transfer #{transfer.id}
              <span className={`badge ${STATUS_COLORS[transfer.status] || 'badge-info'}`}>
                {transfer.status}
              </span>
            </h1>
            <p className="text-gray-500 mt-1 font-mono text-sm">{transfer.transferId}</p>
          </div>
        </div>
      </div>

      {/* Core details */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Transfer Details</h2>
        </div>
        <div className="card-body">
          <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Field label="Transfer ID"    value={transfer.transferId}   mono />
            <Field label="Correlation ID" value={transfer.correlationId} mono />
            <Field label="End-to-End ID"  value={transfer.endToEndId}   mono />
            <Field label="Quote ID"       value={transfer.quoteId}      mono />
            <Field label="Payer FSP"      value={transfer.payerFsp} />
            <Field label="Payee FSP"      value={transfer.payeeFsp} />
            <Field label="Amount"         value={transfer.amount ? `${transfer.amount} ${transfer.currency}` : null} />
            <Field label="Status"         value={transfer.status} />
            <Field label="Status Detail"  value={transfer.statusDetail} />
            <Field label="Fulfilment"     value={transfer.fulfilment} mono />
            <Field label="Expiration"     value={formatDate(transfer.expiration, 'PPpp')} />
            <Field label="Commit Notified At" value={formatDate(transfer.commitNotifiedAt, 'PPpp')} />
            <Field label="Created At"     value={formatDate(transfer.createdAt, 'PPpp')} />
            <Field label="Updated At"     value={formatDate(transfer.updatedAt, 'PPpp')} />
          </dl>
        </div>
      </div>

      {/* Callback payload */}
      {transfer.callbackPayload && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Callback Payload</h2>
          </div>
          <div className="card-body">
            <pre className="bg-gray-50 rounded-lg p-4 text-xs font-mono overflow-auto max-h-96 whitespace-pre-wrap break-all">
              {(() => {
                try { return JSON.stringify(JSON.parse(transfer.callbackPayload), null, 2); }
                catch { return transfer.callbackPayload; }
              })()}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferDetail;
